/**
 * Client-side coupon-pool CSV parser.
 *
 * MIRRORS backend/core/pool_parser.py exactly so the wizard can
 * validate row count and column choice INSTANTLY, without a server
 * round-trip. The backend re-runs its own parser + exact-count check
 * on create — this file is a UX accelerator, not a security boundary.
 *
 * Real-world vendor CSVs come in five common shapes; each is
 * exercised by the test file `pool_parser.test.js`:
 *
 *   1) Single column, no header  (legacy)
 *   2) Single column with header "Coupon Code"     (our template)
 *   3) 2 cols: "index, code"        (POS export — the reported bug)
 *   4) 2 cols: "code, notes"        (Shopify-style)
 *   5) N cols with delimiter=";" or "\t"
 *
 * Never returns null; on empty input returns
 *   { codes: [], invalid: [] }.
 */

const POOL_CODE_MAX_LEN = 64;

const CODE_HEADER_TIERS = [
  { name: "has_code",   rx: /\bcode\b/i },
  { name: "has_value",  rx: /\bvalue\b/i },
  { name: "has_coupon", rx: /\b(coupon|voucher|promo)\b/i },
];

const INDEX_HEADER_RE = /\b(number|no\.?|index|idx|serial|s\.?no|sno|sr|sl\.?no|slno|#|row)\b/i;

/**
 * Detect the delimiter used in a CSV blob by counting occurrences of
 * candidate delimiters on the FIRST non-empty line only (safer than
 * whole-file counts when a code accidentally contains one of them).
 * Falls back to comma.
 */
const detectDelimiter = (sample) => {
  const firstLine = (sample || "")
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .find((l) => l.trim().length > 0) || "";
  const candidates = [",", ";", "\t", "|"];
  let best = ",";
  let bestCount = -1;
  for (const d of candidates) {
    const n = (firstLine.match(new RegExp(d === "\t" ? "\\t" : d.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g")) || []).length;
    if (n > bestCount) { best = d; bestCount = n; }
  }
  return best;
};

/**
 * Minimal RFC-4180-ish CSV row splitter. Handles double-quoted fields
 * with embedded delimiters and escaped quotes. Enough for the
 * one-column-of-codes files we actually accept — we intentionally do
 * NOT ship a full CSV lib here.
 */
const splitRow = (line, delim) => {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i += 1; } else { inQ = false; }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQ = true;
    } else if (ch === delim) {
      out.push(cur); cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((c) => c.trim());
};

const iterCsvRows = (raw) => {
  if (!raw) return [];
  const stripped = raw.replace(/^\uFEFF/, "");
  const delim = detectDelimiter(stripped);
  const lines = stripped.split(/\r?\n/);
  const rows = [];
  for (const line of lines) {
    if (!line || !line.trim()) continue;
    rows.push(splitRow(line, delim));
  }
  return rows;
};

const looksLikeHeader = (cells) => {
  const nonEmpty = cells.map((c) => (c || "").trim()).filter(Boolean);
  if (nonEmpty.length === 0) return false;
  const joined = nonEmpty.join(" ").toLowerCase();
  if (CODE_HEADER_TIERS.some(({ rx }) => rx.test(joined))) return true;
  if (INDEX_HEADER_RE.test(joined)) return true;
  // Pure-text row with any moderately long cell → almost certainly labels.
  const noDigits = nonEmpty.every((c) => !/\d/.test(c));
  if (noDigits && nonEmpty.some((c) => c.length > 4)) return true;
  return false;
};

const scoreColumnByHeader = (headerCells) => {
  return headerCells.map((raw) => {
    const text = (raw || "").trim();
    const penalty = INDEX_HEADER_RE.test(text) ? -50 : 0;
    let boost = 0;
    for (let t = 0; t < CODE_HEADER_TIERS.length; t += 1) {
      if (CODE_HEADER_TIERS[t].rx.test(text)) { boost = 30 - t * 10; break; }
    }
    return penalty + boost;
  });
};

const scoreColumnByValues = (rows, col) => {
  if (!rows.length) return 0;
  let total = 0;
  let numericSequential = 0;
  let letterContaining = 0;
  let nonEmpty = 0;
  let prev = null;
  for (const r of rows) {
    if (col >= r.length) continue;
    const cell = (r[col] || "").trim();
    if (!cell) continue;
    nonEmpty += 1;
    if (/^\d+$/.test(cell)) {
      const asInt = parseInt(cell, 10);
      if (prev !== null && asInt === prev + 1) numericSequential += 1;
      prev = asInt;
      if (cell.length <= 4) total -= 5;
    } else {
      prev = null;
      if (/[A-Za-z]/.test(cell)) { letterContaining += 1; total += 3; }
    }
  }
  if (nonEmpty && numericSequential >= Math.max(3, Math.floor(nonEmpty / 2))) {
    total -= 100;
  }
  if (letterContaining >= Math.max(1, Math.floor(nonEmpty / 2))) {
    total += 20;
  }
  return total;
};

const normalizeCode = (cell) => (cell || "").trim().toUpperCase();

/**
 * Parse a raw CSV / newline blob into normalised, deduplicated code
 * list.
 *
 * @returns { codes: string[], invalid: Array<{row:number,reason:string,value:string}> }
 */
export const parsePoolCodes = (raw) => {
  let rows = iterCsvRows(raw || "");
  if (!rows.length) return { codes: [], invalid: [] };
  rows = rows.map((r) => r.map((c) => (c || "").trim()));
  const maxCols = rows.reduce((m, r) => Math.max(m, r.length), 1);

  let headerRow = null;
  let bodyStart = 0;
  if (maxCols >= 1 && looksLikeHeader(rows[0])) {
    headerRow = rows[0];
    bodyStart = 1;
  }
  const body = rows.slice(bodyStart);

  let chosenCol = 0;
  if (maxCols > 1) {
    const headerScores = headerRow
      ? scoreColumnByHeader(headerRow)
      : new Array(maxCols).fill(0);
    const valueScores = new Array(maxCols).fill(0).map((_, c) => scoreColumnByValues(body, c));
    const combined = new Array(maxCols).fill(0).map((_, c) => headerScores[c] + valueScores[c]);
    const bestScore = Math.max(...combined);
    if (bestScore <= 0) {
      // Every column looked equally uninformative → prefer the LAST
      // column (POS "index, code" is far more common than "code, notes").
      chosenCol = maxCols - 1;
    } else {
      chosenCol = combined.indexOf(bestScore);
    }
  }

  const seen = new Set();
  const codes = [];
  const invalid = [];
  body.forEach((row, i) => {
    const rowIdx = bodyStart + i + 1;
    const cell = chosenCol < row.length ? row[chosenCol] : "";
    const norm = normalizeCode(cell);
    if (!norm) return;
    if (CODE_HEADER_TIERS.some(({ rx }) => rx.test(norm))) {
      invalid.push({ row: rowIdx, reason: "looks_like_header", value: norm.slice(0, 32) });
      return;
    }
    if (norm.length > POOL_CODE_MAX_LEN) {
      invalid.push({ row: rowIdx, reason: "too_long", value: norm.slice(0, 32) });
      return;
    }
    if (seen.has(norm)) return;
    seen.add(norm);
    codes.push(norm);
  });

  return { codes, invalid };
};

export const POOL_MAX_CODE_LEN = POOL_CODE_MAX_LEN;
